import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-4 py-16">
      <div className="max-w-2xl w-full text-center">
        {/* 404 Illustration */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-6">
            <Search className="w-16 h-16 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-8xl font-bold text-gray-900 dark:text-white mb-4">
            404
          </h1>
          <h2 className="text-3xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Page Not Found
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            Oops! The page you're looking for doesn't exist. It might have been moved or deleted.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
          {user ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
            >
              <Home className="w-5 h-5" />
              Go to Dashboard
            </button>
          ) : (
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
            >
              <Home className="w-5 h-5" />
              Go to Home
            </button>
          )}
        </div>

        {/* Helpful Links - Only show for authenticated users */}
        {user && (
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Looking for something? Try these:
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate('/articles')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Articles
              </button>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <button
                onClick={() => navigate('/assets')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Assets
              </button>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <button
                onClick={() => navigate('/user/profile')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Profile
              </button>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <button
                onClick={() => navigate('/billing')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Billing
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotFoundPage;
